"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useState, useEffect } from "react";
import { CustomButton } from "@/components/ui/custom-button";
import { ReadMoreButton } from "@/components/ui/read-more-button";
import { CTABanner } from "@/components/shared/cta_banner";

interface CaseStudyItem {
	title: string;
	text: string;
	imageUrl: string;
	link: string;
	openModal?: string;
}

interface DynamicCaseStudiesProps {
	items: CaseStudyItem[];
	className?: string;
	start_with_a_pilot?: boolean;
	showViewAll?: boolean;
	showSeeMore?: boolean;
	showKeepReading?: boolean;
	onItemClick?: (item: CaseStudyItem) => void;
}

export function DynamicCaseStudies({ items, className = "", start_with_a_pilot = false, showViewAll = true, showSeeMore = false, showKeepReading = false, onItemClick }: DynamicCaseStudiesProps) {
	const [itemsToShow, setItemsToShow] = useState(9);
	const [newlyLoadedItems, setNewlyLoadedItems] = useState<Set<number>>(new Set());
	const [showPilotCard, setShowPilotCard] = useState(false);
	const visibleItems = items.slice(0, itemsToShow);
	const hasMoreItems = itemsToShow < items.length;
	const shouldShowBanner = items.length > 6;
	const shouldShowLoadMore = items.length > 9;

	const handleLoadMore = () => {
		const previousCount = itemsToShow;
		const newCount = Math.min(itemsToShow + 6, items.length);
		
		// Track which items are newly loaded
		const newItems = new Set<number>();
		for (let i = previousCount; i < newCount; i++) {
			newItems.add(i);
		}
		setNewlyLoadedItems(newItems);
		
		setItemsToShow(newCount);
		
		// Check if pilot card should appear after loading more
		if (start_with_a_pilot && newCount >= items.length) {
			setShowPilotCard(true);
		}
	};

	// Clear the newly loaded items after animation completes
	useEffect(() => {
		if (newlyLoadedItems.size > 0) {
			const timer = setTimeout(() => {
				setNewlyLoadedItems(new Set());
			}, 600); // Match animation duration
			return () => clearTimeout(timer);
		}
	}, [newlyLoadedItems]);

	// Clear the pilot card animation state after animation completes
	useEffect(() => {
		if (showPilotCard) {
			const timer = setTimeout(() => {
				setShowPilotCard(false);
			}, 600); // Match animation duration
			return () => clearTimeout(timer);
		}
	}, [showPilotCard]);

	return (
		<section className={`py-12 sm:py-16 md:py-20 ${className}`}>
			<div className="max-w-7xl lg:mx-auto px-4 sm:px-6 md:px-10">
				{/* Header with Keep Reading or View all button */}
				{showKeepReading ? (
					<div className="mb-8 sm:mb-12 md:mb-16">
						<h2 className="text-2xl sm:text-3xl font-bold text-gray-900 md:text-4xl">
							Keep Reading
						</h2>
					</div>
				) : showViewAll && (
					<div className="hidden md:flex justify-end mb-6 sm:mb-8">
						<Link href="/about/case-studies">
							<CustomButton variant="outline" size="lg">
								View all
							</CustomButton>
						</Link>
					</div>
				)}
				
				{/* First 6 items */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-12 sm:gap-y-16 md:gap-y-24">
					{items.slice(0, 6).map((item, index) => {
						const CardContent = () => (
							<div className="group block h-full cursor-pointer">
							<div className="bg-white transition-all duration-300 ease-in-out group-hover:scale-[1.02] h-full flex flex-col">
								{/* Image */}
								<div className="relative h-48 sm:h-56 md:h-64 w-full overflow-hidden rounded-2xl sm:rounded-3xl">
									<Image
										src={item.imageUrl}
										alt={item.title}
										fill
										className="object-cover group-hover:scale-105 transition-transform duration-300"
									/>
								</div>
								
								{/* Content */}
								<div className="pt-3 sm:pt-4 flex-1 flex flex-col">
									<h5 className="mb-1 sm:mb-2 group-hover:text-ss-blue transition-colors text-lg sm:text-xl font-semibold text-gray-900">
										{item.title}
									</h5>
									<p className="text-xs sm:text-sm leading-relaxed max-h-16 sm:max-h-20 overflow-hidden flex-1">
										{item.text}
									</p>
									<div className="mt-4 sm:mt-6">
										<ReadMoreButton showArrow={false} size="lg" />
									</div>
								</div>
							</div>
						</div>
						);

						if (item.openModal && onItemClick) {
							return (
								<div
									key={index}
									onClick={() => onItemClick(item)}
								>
									<CardContent />
								</div>
							);
						}

						return (
							<Link 
								key={index} 
								href={item.link}
							>
								<CardContent />
							</Link>
						);
					})}
				</div>

				{/* Banner - appears between first 6 and remaining items */}
				{items.length > 6 && (
					<div className="mt-8 sm:mt-12 md:mt-16 mb-8 sm:mb-12 md:mb-16">
						<CTABanner
							title="Empower your students to succeed"
							description="Join leading institutions in transforming student learning outcomes with StudyStash's AI-powered platform."
							buttonText="Book a demo"
							buttonHref="/book-a-demo"
						/>
					</div>
				)}

				{/* Remaining items (7+) */}
				{items.length > 6 && (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-12 sm:gap-y-16 md:gap-y-24">
						{items.slice(6, itemsToShow).map((item, index) => {
							const actualIndex = index + 6;
							const isNewlyLoaded = newlyLoadedItems.has(actualIndex);
							
							const CardContent = () => (
								<div className={`group block h-full transition-all duration-600 ease-out cursor-pointer ${
									isNewlyLoaded 
										? 'animate-fade-in-up opacity-0' 
										: 'opacity-100'
								}`}
								style={{
									animationDelay: isNewlyLoaded ? `${(index % 6) * 100}ms` : '0ms',
									animationFillMode: 'forwards'
								}}>
									<div className="bg-white transition-all duration-300 ease-in-out group-hover:scale-[1.02] h-full flex flex-col">
									{/* Image */}
									<div className="relative h-48 sm:h-56 md:h-64 w-full overflow-hidden rounded-2xl sm:rounded-3xl">
										<Image
											src={item.imageUrl}
											alt={item.title}
											fill
											className="object-cover group-hover:scale-105 transition-transform duration-300"
										/>
									</div>
									
									{/* Content */}
									<div className="pt-3 sm:pt-4 flex-1 flex flex-col">
										<h5 className="mb-1 sm:mb-2 group-hover:text-ss-blue transition-colors text-lg sm:text-xl font-semibold text-gray-900">
											{item.title}
										</h5>
										<p className="text-xs sm:text-sm leading-relaxed max-h-16 sm:max-h-20 overflow-hidden flex-1">
											{item.text}
										</p>
										<div className="mt-4 sm:mt-6">
											<ReadMoreButton showArrow={false} size="lg" />
										</div>
									</div>
								</div>
							</div>
							);

							if (item.openModal && onItemClick) {
								return (
									<div
										key={actualIndex}
										onClick={() => onItemClick(item)}
									>
										<CardContent />
									</div>
								);
							}

							return (
								<Link 
									key={actualIndex} 
									href={item.link}
								>
									<CardContent />
								</Link>
							);
						})}
						
						{/* Pilot card - appears at the end if no remaining items */}
						{start_with_a_pilot && !hasMoreItems && (
							<div className={`bg-linear-to-bl from-ss-light-green via-ss-green to-ss-blue rounded-2xl sm:rounded-3xl h-full flex flex-col p-4 sm:p-6 md:p-8 transition-all duration-600 ease-out ${
								showPilotCard 
									? 'animate-fade-in-up opacity-0' 
									: 'opacity-100'
							}`}
							style={{
								animationDelay: showPilotCard ? '300ms' : '0ms',
								animationFillMode: 'forwards'
							}}>
								<div className="flex-1 flex flex-col justify-center text-left text-white">
									<h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 text-white">
										Start with a pilot programme
									</h3>
									<p className="text-sm sm:text-base md:text-lg mb-4 sm:mb-6 text-white opacity-90">
										Choose 2-3 modules, witness the impact on student attainment and lecturer insights, then scale what works.
									</p>
									<p className="text-xs sm:text-sm mb-6 sm:mb-8 text-white opacity-80">
										No complex integrations, no staff training requirements, no disruption to existing workflows.
									</p>
									<Link href="/book-a-demo" className="mt-2 sm:mt-4">
										<CustomButton 
											variant="primary" 
											size="md"
										>
											Request your pilot programme
										</CustomButton>
									</Link>
								</div>
							</div>
						)}
					</div>
				)}

				{/* Mobile View all button */}
				{showViewAll && (
					<div className="md:hidden mt-6 sm:mt-8 text-center">
						<Link href="/about/case-studies">
							<CustomButton variant="outline" size="lg">
								View all
							</CustomButton>
						</Link>
					</div>
				)}
				
				{/* Load more button - centered at bottom */}
				{items.length > 9 && hasMoreItems && (
					<div className="mt-12 sm:mt-16 md:mt-24 text-center">
						<CustomButton 
							variant="outline" 
							size="lg"
							onClick={handleLoadMore}
						>
							Load more posts
						</CustomButton>
					</div>
				)}
			</div>
		</section>
	);
}
